import React, { useEffect, useState } from "react";
import { Center, Flex, VStack } from "@chakra-ui/react";
import useAuth from "../../hooks/useAuth";
import RegistrationStepper from "../Pages/Authentication/RegistrationStepper";
import { LoginRequestDto, RegistrationDto } from "../../services/Dtos";
import FormContainer from "../CustomUi/FormContainer";
import { useFormChanges } from "../../hooks/useFormChanges";
import { useFormFieldHandler } from "../../hooks/useFormFieldHandler";

import {
  validateEmail,
  validatePassword,
  validateRequired,
  validateNigerianPhoneNumber,
} from "../../services/validation";

import PersonalDetailsRegForm from "./PersonalDetailsRegForm";
import AuthRegForm from "../CustomUi/AuthRegForm";
import SchoolInfoRegForm from "../CustomUi/SchoolInfoRegForm";
import useIcons from "../../hooks/useIcons";
import { Gender } from "../../services/Enums";
import TermsAgreementForm from "./TermsAgreementForm";

const MultiStepForm: React.FC = () => {
  const { register, loading, error } = useAuth();
  const [step, setStep] = useState<number>(1);
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  const [registrationData, setRegistrationData] = useState<RegistrationDto>({
    firstName: "",
    lastName: "",
    gender: 0 as Gender,
    email: "",
    password: "",
    confirmPassword: "",
    schoolName: "",
    schoolAddress: "",
    schoolPhoneNumber: "",
    schoolEmail: undefined,
    schoolWebsite: "",
    agreeToTerms: false,
    agreeToPrivacyPolicy: false,
  });

  const icons = useIcons();

  // Define field configurations for form validation
  const fieldConfigs = {
    firstName: { type: "text" as const, required: true, maxLength: 50 },
    lastName: { type: "text" as const, required: true, maxLength: 50 },
    gender: { type: "select" as const, required: true },
    email: { type: "email" as const, required: true },
    password: { type: "text" as const, required: true },
    confirmPassword: { type: "text" as const, required: true },
    schoolName: { type: "text" as const, required: true, maxLength: 100 },
    schoolAddress: { type: "text" as const, required: true, maxLength: 200 },
    schoolPhoneNumber: { type: "phone" as const, required: true },
    schoolEmail: { type: "email" as const, required: false },
    schoolWebsite: { type: "text" as const, required: false },
    agreeToTerms: { type: "boolean" as const, required: true },
    agreeToPrivacyPolicy: { type: "boolean" as const, required: true },
  };

  // Use the form changes hook
  const { hasChanges, validateField, validateAllFields, hasValidationErrors } =
    useFormChanges({
      fields: fieldConfigs,
      initialData: null,
      currentData: registrationData,
      isEditMode: false,
    });

  // Use the form field handler hook
  const { handleFieldChange } = useFormFieldHandler({
    setFormState: setRegistrationData,
    setTouchedFields,
    numberFields: ["gender"],
    booleanFields: ["agreeToTerms", "agreeToPrivacyPolicy"],
    integerFields: [],
  });

  // Custom validation functions
  const validateEmailField = (value: string): string => {
    if (!validateRequired(value)) {
      return "Email is required";
    }
    if (!validateEmail(value)) {
      return "Valid email is required";
    }
    return "";
  };

  const validatePasswordField = (value: string): string => {
    if (!validateRequired(value)) {
      return "Password is required";
    }
    if (!validatePassword(value)) {
      return "Password must be at least 8 characters and contain at least one uppercase letter, one lowercase letter, one number and one special character";
    }
    return "";
  };

  const validateConfirmPasswordField = (value: string): string => {
    if (!validateRequired(value)) {
      return "Confirm password is required";
    }
    if (value !== registrationData.password) {
      return "Passwords do not match";
    }
    return "";
  };

  const validatePhoneField = (value: string): string => {
    if (!validateRequired(value)) {
      return "Phone number is required";
    }
    if (!validateNigerianPhoneNumber(value)) {
      return "Invalid Nigerian phone number";
    }
    return "";
  };

  const validateSchoolEmailField = (value: string): string => {
    if (!value) return ""; // Optional field
    if (!validateEmail(value)) {
      return "Invalid email address";
    }
    return "";
  };

  const validateTermsField = (value: boolean): string => {
    if (!value) {
      return "You must agree to the Terms of Service";
    }
    return "";
  };

  const validatePrivacyField = (value: boolean): string => {
    if (!value) {
      return "You must agree to the Privacy Policy";
    }
    return "";
  };

  // Real-time validation using the hook's validateField
  const validateFieldByName = (name: string, value: any): string => {
    const fieldKey = name as keyof RegistrationDto;

    // Custom validation for specific fields
    if (name === "email") {
      return validateEmailField(value);
    }
    if (name === "password") {
      return validatePasswordField(value);
    }
    if (name === "confirmPassword") {
      return validateConfirmPasswordField(value);
    }
    if (name === "schoolPhoneNumber") {
      return validatePhoneField(value);
    }
    if (name === "schoolEmail") {
      return validateSchoolEmailField(value);
    }
    if (name === "agreeToTerms") {
      return validateTermsField(value);
    }
    if (name === "agreeToPrivacyPolicy") {
      return validatePrivacyField(value);
    }

    return validateField(fieldKey, value);
  };

  const showFieldError = (fieldName: string): boolean => {
    const error = validateFieldByName(
      fieldName,
      registrationData[fieldName as keyof RegistrationDto]
    );
    return !!(error && touchedFields.has(fieldName));
  };

  const getFieldError = (fieldName: string): string => {
    return validateFieldByName(
      fieldName,
      registrationData[fieldName as keyof RegistrationDto]
    );
  };

  const getCurrentStepFields = (): string[] => {
    switch (step) {
      case 1:
        return ["firstName", "lastName", "gender", "email"];
      case 2:
        return ["password", "confirmPassword"];
      case 3:
        return [
          "schoolName",
          "schoolAddress",
          "schoolPhoneNumber",
          "schoolEmail",
          "schoolWebsite",
        ];
      case 4:
        return ["agreeToTerms", "agreeToPrivacyPolicy"];
      default:
        return [];
    }
  };

  const hasCurrentStepChanges = (): boolean => {
    const currentFields = getCurrentStepFields();
    return currentFields.some((fieldName) => {
      const value = registrationData[fieldName as keyof RegistrationDto];
      if (
        fieldName === "agreeToTerms" ||
        fieldName === "agreeToPrivacyPolicy"
      ) {
        return value === true;
      }
      return !!value;
    });
  };

  const hasCurrentStepValidationErrors = (): boolean => {
    const currentFields = getCurrentStepFields();
    return currentFields.some((fieldName) => {
      const error = getFieldError(fieldName);
      return showFieldError(fieldName) && error !== "";
    });
  };

  const isNextButtonDisabled = (): boolean => {
    return !hasCurrentStepChanges() || hasCurrentStepValidationErrors();
  };

  // Calculate activeStep for the stepper (0-based index)
  const activeStep = step - 1;

  const [, setLoginData] = useState<LoginRequestDto>({
    username: "",
    password: "",
  });

  useEffect(() => {
    setLoginData({
      username: registrationData.email,
      password: registrationData.password,
    });
  }, [registrationData]);

  const handleNextStep = () => {
    // Only validate current step fields
    const currentFields = getCurrentStepFields();
    let hasErrors = false;

    currentFields.forEach((fieldName) => {
      const error = validateFieldByName(
        fieldName,
        registrationData[fieldName as keyof RegistrationDto]
      );
      if (error) {
        hasErrors = true;
      }
    });

    if (!hasErrors) {
      setStep(step + 1);
    }
  };

  const handlePreviousStep = () => {
    setStep(step - 1);
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check if form has changes
    if (!hasChanges) {
      return;
    }

    // Validate all fields
    const errors = validateAllFields();
    if (hasValidationErrors(errors)) {
      return;
    }

    await register(registrationData);
  };

  const steps = [
    {
      title: "Your Details",
      description: "Name and Email",
      icon: icons.people.user,
    },
    {
      title: "Password",
      description: "Choose a Secure Password",
      icon: icons.utility.shield,
    },
    {
      title: "School",
      description: "Setup School Account",
      icon: icons.main.school,
    },
    {
      title: "Complete",
      description: "Complete Registration",
      icon: icons.utility.check,
    },
  ];

  const getFormHeader = (): { heading: string; description: string } => {
    switch (step) {
      case 1:
        return {
          heading: "Getting Started!",
          description: "Please enter your details.",
        };
      case 2:
        return {
          heading: "Secure Your Account",
          description: "Choose a strong password",
        };
      case 3:
        return {
          heading: "Setup School Account",
          description: "Please enter your school details.",
        };
      case 4:
        return {
          heading: "Complete Registration",
          description:
            "Please review, agree to our terms and complete your registration.",
        };
      default:
        return {
          heading: "Getting Started",
          description: "Please enter your details.",
        };
    }
  };

  return (
    <Flex h={"full"} flexDir={"column"} alignItems={"space-between"} w={"full"}>
      <Center flex={1}>
        <FormContainer
          heading={getFormHeader().heading}
          description={getFormHeader().description}
        >
          {step === 1 && (
            <PersonalDetailsRegForm
              handleChange={handleFieldChange}
              errors={{
                firstName: showFieldError("firstName")
                  ? getFieldError("firstName")
                  : "",
                lastName: showFieldError("lastName")
                  ? getFieldError("lastName")
                  : "",
                gender: showFieldError("gender") ? getFieldError("gender") : "",
                email: showFieldError("email") ? getFieldError("email") : "",
              }}
              handleNextStep={handleNextStep}
              loading={loading}
              firstName={registrationData.firstName}
              lastName={registrationData.lastName}
              email={registrationData.email}
              gender={registrationData.gender}
              isNextDisabled={isNextButtonDisabled()}
            />
          )}

          {step === 2 && (
            <AuthRegForm
              handleChange={handleFieldChange}
              errors={{
                password: showFieldError("password")
                  ? getFieldError("password")
                  : "",
                confirmPassword: showFieldError("confirmPassword")
                  ? getFieldError("confirmPassword")
                  : "",
              }}
              handleNextStep={handleNextStep}
              loading={loading}
              password={registrationData.password}
              confirmPassword={registrationData.confirmPassword}
              handlePreviousStep={handlePreviousStep}
              isNextDisabled={isNextButtonDisabled()}
            />
          )}

          {step === 3 && (
            <SchoolInfoRegForm
              handleChange={handleFieldChange}
              errors={{
                schoolName: showFieldError("schoolName")
                  ? getFieldError("schoolName")
                  : "",
                schoolAddress: showFieldError("schoolAddress")
                  ? getFieldError("schoolAddress")
                  : "",
                schoolPhoneNumber: showFieldError("schoolPhoneNumber")
                  ? getFieldError("schoolPhoneNumber")
                  : "",
                schoolEmail: showFieldError("schoolEmail")
                  ? getFieldError("schoolEmail")
                  : "",
                schoolWebsite: showFieldError("schoolWebsite")
                  ? getFieldError("schoolWebsite")
                  : "",
              }}
              handleNextStep={handleNextStep}
              loading={loading}
              schoolName={registrationData.schoolName}
              schoolAddress={registrationData.schoolAddress}
              schoolPhoneNumber={registrationData.schoolPhoneNumber}
              schoolEmail={registrationData.schoolEmail}
              schoolWebsite={registrationData.schoolWebsite}
              handlePreviousStep={handlePreviousStep}
              isNextDisabled={isNextButtonDisabled()}
            />
          )}

          {step === 4 && (
            <VStack gap={6} align="stretch">
              <TermsAgreementForm
                loading={loading}
                registrationData={registrationData}
                setRegistrationData={setRegistrationData}
                handleRegister={handleRegister}
                handlePreviousStep={handlePreviousStep}
                error={error}
              />
            </VStack>
          )}
        </FormContainer>
      </Center>
      <Center as="footer" w={"full"} py={5} px={5} h={100}>
        <RegistrationStepper activeStep={activeStep} steps={steps} />
      </Center>
    </Flex>
  );
};

export default MultiStepForm;
